import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calendar, MessageSquare, Briefcase, Clock, ArrowRight, Loader2, HelpCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import OnboardingTutorial, { useOnboarding } from '../../components/OnboardingTutorial';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t, language } = useLanguage();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [becomingProvider, setBecomingProvider] = useState(false);
  
  // Onboarding tutorial
  const { showOnboarding, setShowOnboarding, resetOnboarding } = useOnboarding('client');

  // SAFETY: Redirect admin/provider away from client dashboard
  useEffect(() => {
    if (user && user.role === 'admin') {
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    if (user && user.role === 'provider') {
      navigate('/provider/dashboard', { replace: true });
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, catRes] = await Promise.all([
          fetch(`${API_URL}/api/dashboard/client`, {
            headers: {
              'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
            },
          }),
          fetch(`${API_URL}/api/config/categories`)
        ]);

        if (dashRes.ok) {
          const data = await dashRes.json();
          setDashboardData(data);
        }
        
        if (catRes.ok) {
          const catData = await catRes.json();
          setCategories(catData.categories);
        }
      } catch (error) {
        console.error('Error fetching dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleBecomeProvider = async () => {
    if (!selectedCategory) {
      toast.error('Please select a service category');
      return;
    }
    
    setBecomingProvider(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/become-provider`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ service_category: selectedCategory }),
      });
      
      if (response.ok) {
        toast.success(t('common.success'));
        window.location.href = '/provider/dashboard';
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setBecomingProvider(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: t('booking.pending'), variant: 'secondary' },
      confirmed: { label: t('booking.confirmed'), variant: 'default' },
      paid: { label: t('booking.paid'), variant: 'default' },
      completed: { label: t('booking.completed'), variant: 'outline' },
      cancelled: { label: t('booking.cancelled'), variant: 'destructive' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getCategoryName = (key) => {
    return categories[key]?.[language] || categories[key]?.['en'] || key;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="client-dashboard">
      <div className="container-app">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mb-2">
            {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground">
            {t('home.heroSubtitle')}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">
                    {dashboardData?.upcoming_bookings?.length || 0}
                  </p>
                  <p className="text-muted-foreground text-sm">{t('dashboard.upcomingBookings')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-sage/20 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-sage" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">
                    {dashboardData?.past_bookings_count || 0}
                  </p>
                  <p className="text-muted-foreground text-sm">{t('dashboard.pastBookings')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">
                    {dashboardData?.unread_messages || 0}
                  </p>
                  <p className="text-muted-foreground text-sm">{t('nav.messages')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Upcoming Bookings */}
          <div className="lg:col-span-2">
            <Card className="dashboard-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">{t('dashboard.upcomingBookings')}</CardTitle>
                <Button 
                  variant="ghost" 
                  className="text-accent"
                  onClick={() => navigate('/bookings')}
                  data-testid="view-all-bookings"
                >
                  {t('home.viewAll')}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {dashboardData?.upcoming_bookings?.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground mb-4">{t('common.noResults')}</p>
                    <Button
                      className="btn-primary"
                      onClick={() => navigate('/providers')}
                      data-testid="find-provider-btn"
                    >
                      {t('dashboard.findProvider')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {dashboardData?.upcoming_bookings?.map((booking) => (
                      <div
                        key={booking.booking_id}
                        className="flex items-center justify-between p-4 rounded-xl bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                        onClick={() => navigate(`/bookings/${booking.booking_id}`)}
                        data-testid={`booking-${booking.booking_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                            <Briefcase className="w-6 h-6 text-accent" />
                          </div>
                          <div>
                            <p className="font-medium">{booking.provider_name}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="w-3.5 h-3.5" />
                              <span>{booking.date} {booking.start_time}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(booking.status)}
                          <p className="text-sm text-muted-foreground mt-1">
                            {booking.total_price?.toFixed(0)}€
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="font-heading">{t('dashboard.quickActions')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/providers')}
                  data-testid="quick-find-provider"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  {t('dashboard.findProvider')}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/messages')}
                  data-testid="quick-messages"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {t('dashboard.myMessages')}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/bookings')}
                  data-testid="quick-bookings"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {t('nav.bookings')}
                </Button>
              </CardContent>
            </Card>

            {/* Become a Provider CTA */}
            <Card className="dashboard-card bg-accent/5 border-accent/20">
              <CardContent className="pt-6">
                <Briefcase className="w-10 h-10 text-accent mb-4" />
                <h3 className="font-heading font-semibold mb-2">{t('home.ctaTitle')}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('home.ctaSubtitle')}
                </p>
                
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="mb-3">
                    <SelectValue placeholder={t('home.allCategories')} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(categories).map((key) => (
                      <SelectItem key={key} value={key}>
                        {getCategoryName(key)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button
                  className="btn-primary w-full"
                  onClick={handleBecomeProvider}
                  disabled={becomingProvider || !selectedCategory}
                  data-testid="become-provider-btn"
                >
                  {becomingProvider ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    t('nav.becomeProvider')
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Help button to restart tutorial */}
      <Button
        variant="outline"
        size="icon"
        className="fixed bottom-6 right-6 w-12 h-12 rounded-full shadow-lg bg-background hover:bg-accent hover:text-white z-40"
        onClick={resetOnboarding}
        title="Aide / Tutoriel"
        data-testid="help-tutorial-btn"
      >
        <HelpCircle className="w-6 h-6" />
      </Button>
      
      {/* Onboarding Tutorial */}
      <OnboardingTutorial 
        userRole="client"
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => setShowOnboarding(false)}
      />
    </div>
  );
}
